import React, { useState, useEffect } from "react";
import { Modal, Card, Row, Col, Form, Button } from "react-bootstrap";
import { FaTrash, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import Select from "react-select";
import WhatsAppAPI from "../../api/WhatsAppAPI";

const TagModal = ({ show, onHide, editData = null, viewOnly = false }) => {
  const [tagName, setTagName] = useState(editData?.name || "");
  const [firstMessage, setFirstMessage] = useState(
    editData?.first_message === "Yes" || false,
  );
  const [status, setStatus] = useState(
    editData?.status !== undefined ? editData.status : true,
  );
  const [rules, setRules] = useState(editData?.auto_tag_rules || []);
  const [isSending, setIsSending] = useState(false);
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const userOptions = users.map((user) => ({
    value: user.id,
    label: `${user.firstname} ${user.lastname}`,
  }));

  useEffect(() => {
    const getUsers = async () => {
      const response = await WhatsAppAPI.fetchUsers();

      // filter only USER role
      const filtered = response.filter((user) => user.userrole === "USER");

      setUsers(filtered);
    };

    getUsers();
  }, []);

  useEffect(() => {
    if (editData) {
      setTagName(editData.name || "");
      setFirstMessage(editData.first_message === "Yes");
      setStatus(editData.status !== undefined ? editData.status : true);
      setRules(editData.auto_tag_rules || []);
      if (editData.ownerids && users.length > 0) {
        const mappedUsers = users
          .filter((user) => editData.ownerids.includes(user.id))
          .map((user) => ({
            value: user.id,
            label: `${user.firstname} ${user.lastname}`,
          }));

        setSelectedUsers(mappedUsers);
      }
    } else {
      resetForm();
    }
  }, [editData]);

  const handleAddRule = () => {
    setRules([...rules, { keyword: "", match_type: "exact" }]);
  };

  const handleRemoveRule = (index) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const handleRuleChange = (index, field, value) => {
    const updated = [...rules];
    updated[index][field] = value;
    setRules(updated);

    if (firstMessage && field === "keyword") {
      const hasValidKeyword = updated.some((rule) => rule.keyword.trim());
      if (hasValidKeyword && errors.rules) {
        setErrors((prev) => {
          const { rules, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const resetForm = () => {
    setTagName("");
    setFirstMessage(false);
    setStatus(true);
    setRules([]);
    setErrors({});
    setIsSending(false);
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const isFormValid = tagName.trim() !== "";

  const validateForm = () => {
    const newErrors = {};
    if (!tagName.trim()) {
      newErrors.tagName = "Tag name is required";
    }
    if (
      firstMessage &&
      (!rules.length || rules.some((rule) => !rule.keyword.trim()))
    ) {
      newErrors.rules =
        "At least one rule with a keyword is required when First Message is enabled";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSending(true);
    const ownerids = selectedUsers.map((user) => user.value);

    const payload = {
      name: tagName,
      status,
      first_message: firstMessage ? "Yes" : "No",
      auto_tag_rules: firstMessage ? rules : [],
      ownerids: ownerids,
    };

    try {
      let response = editData?.id
        ? await WhatsAppAPI.updateTag(editData.id, payload)
        : await WhatsAppAPI.createTag(payload);

      if (response.success) {
        toast.success(`Tag ${editData ? "updated" : "created"} successfully`);
        handleClose();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(`Error ${editData ? "updating" : "creating"} tag`);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editData ? (viewOnly ? "View Tag" : "Edit Tag") : "Create New Tag"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row className="mb-2">
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold mb-1">Tag Name</Form.Label>
                <p className="text-muted small mb-2">
                  Pick something that describes your contact.
                </p>
                <Form.Control
                  type="text"
                  value={tagName}
                  onChange={(e) => setTagName(e.target.value)}
                  placeholder="Enter tag name..."
                  required
                  style={{ height: "36px" }}
                  isInvalid={!!errors.tagName}
                  readOnly={viewOnly}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.tagName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* First Message Toggle */}
          <Row className="mb-2">
            <Col xs={12}>
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="fw-bold mb-1" style={{ color: "#2c3e50" }}>
                      First Message
                    </h6>
                    <p className="text-muted small mb-0">
                      Allows auto tagging if users' first message matches
                    </p>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="firstMessageSwitch"
                      checked={firstMessage}
                      onChange={(e) => {
                        if (!viewOnly) {
                          setFirstMessage(e.target.checked);
                          if (!e.target.checked) {
                            setRules([]);
                            setErrors({});
                            setSelectedUsers([]);
                          }
                        }
                      }}
                      style={{ transform: "scale(1.3)" }}
                      disabled={viewOnly}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          {firstMessage &&
            (viewOnly ? (
              <div className="mb-3">
                <Form.Label className="fw-semibold mb-1">
                  Auto Assigned Owners
                </Form.Label>

                {selectedUsers.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {selectedUsers.map((user) => (
                      <span key={user.value} className="badge bg-primary">
                        {user.label}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted small">No users assigned</p>
                )}
              </div>
            ) : (
              <Row className="mb-3">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold mb-1">
                      Auto Assign Owners
                    </Form.Label>
                    <p className="text-muted small mb-2">
                      Automatically assign selected users when this tag is
                      applied
                    </p>

                    <Select
                      options={userOptions}
                      isMulti
                      placeholder="Select users..."
                      onChange={(selected) => {
                        setSelectedUsers(selected || []);
                      }}
                      value={selectedUsers}
                    />
                  </Form.Group>
                </Col>
              </Row>
            ))}
          {/* Auto Tag Rules */}
          {firstMessage && (
            <>
              <Row className="mb-3">
                <Col xs={12}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="fw-bold mb-1" style={{ color: "#2c3e50" }}>
                        Auto Tag Rules
                      </h6>
                      <p className="text-muted small mb-0">
                        Define keywords to automatically apply this tag
                      </p>
                    </div>
                    {!viewOnly && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleAddRule}
                        className="d-flex ms-auto align-items-center justify-content-center"
                      >
                        <FaPlus size={14} />
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>

              {errors.rules && (
                <div className="text-danger mb-3 small">{errors.rules}</div>
              )}

              {rules.length > 0 ? (
                <div className="mb-3 mx-2">
                  <Row>
                    <Col md={5}>
                      <Form.Label className="small fw-semibold">
                        Keyword
                      </Form.Label>
                    </Col>
                    <Col md={5}>
                      <Form.Label className="small fw-semibold">
                        Match Type
                      </Form.Label>
                    </Col>
                  </Row>

                  {rules.map((rule, index) => (
                    <Row key={index} className="align-items-center mb-2">
                      <Col md={5}>
                        <Form.Control
                          type="text"
                          value={rule.keyword}
                          onChange={(e) =>
                            handleRuleChange(index, "keyword", e.target.value)
                          }
                          placeholder="Enter keyword..."
                          style={{ height: "36px" }}
                          disabled={viewOnly}
                          isInvalid={firstMessage && !rule.keyword.trim()}
                        />
                      </Col>
                      <Col md={5}>
                        <Form.Select
                          value={rule.match_type}
                          onChange={(e) =>
                            handleRuleChange(
                              index,
                              "match_type",
                              e.target.value,
                            )
                          }
                          style={{ height: "36px" }}
                          disabled={viewOnly}
                        >
                          <option value="exact">Exact Match</option>
                          <option value="contains">Contains</option>
                        </Form.Select>
                      </Col>

                      {!viewOnly && (
                        <Col md={2}>
                          <Button
                            variant="danger"
                            size="sm"
                            className="ms-auto delete-node-btn"
                            onClick={() => handleRemoveRule(index)}
                          >
                            <FaTrash />
                          </Button>
                        </Col>
                      )}
                    </Row>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <p>
                    No auto tag rules added yet. Click "Add Rule" to create one.
                  </p>
                </div>
              )}
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          {viewOnly ? "Close" : "Cancel"}
        </Button>
        {!viewOnly && (
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!isFormValid || isSending}
          >
            {isSending ? "Saving..." : editData ? "Update" : "Save"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default TagModal;
